import { computed } from "vue";
import store from "@/store";

/**
 * The two top-level domains the portal is navigated in. Historically the portal was
 * ancestry-only and every render decision keyed off `barcode.endsWith('E')` scattered
 * across components. This is the single source of truth for "which domain is the current
 * profile in", so those checks can be centralised.
 *
 * Domain is derived from the barcode suffix: epigenetics barcodes end in 'E', everything
 * else (LD physical kits, LT uploads) is ancestry. The php api also has a profile
 * `type` (ANCESTRY | EPIGENETICS) column, but it is not cleanly exposed to the frontend
 * yet and its name collides with the FOR_ME/FOR_CHILD ownership `type`, so the suffix is
 * the launch source of truth.
 */
export const ANCESTRY = "ANCESTRY";
export const HEALTH = "HEALTH";

/** Domain a specific barcode belongs to. */
export function domainOfBarcode(barcode) {
  return barcode?.endsWith("E") ? HEALTH : ANCESTRY;
}

/** True when a barcode is an epigenetics (health) barcode. */
export function isEpigeneticBarcode(barcode) {
  return domainOfBarcode(barcode) === HEALTH;
}

/**
 * Reactive view of the current profile's domain. Use `isHealthContext` /
 * `isAncestryContext` to gate what the nav and store render.
 */
export function useProfileDomain() {
  const currentBarcode = computed(() => store.getters.profile?.profile?.barcode ?? null);
  const currentDomain = computed(() => domainOfBarcode(currentBarcode.value));
  const isHealthContext = computed(() => currentDomain.value === HEALTH);
  const isAncestryContext = computed(() => currentDomain.value === ANCESTRY);

  return { currentBarcode, currentDomain, isHealthContext, isAncestryContext };
}
